import { AccordionHeader, ReuseAccordion } from "../../ReuseAccordion";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { StatusIcon } from "./StatusIcon";
import { formattedDate } from "../../../../utils/formatDate";
import { InfoItem } from "./InfoItem";
import { NotificationBody } from "./NotificationBody";
import { Activities } from "types/apiTypes";
import { AlertInCircleIcon, CheckInEmptyCircleIcon } from "../../../images";
import { useDeleteCompaniesActivitiesMutation } from "../../../../api";
import { NotificationTypes } from "../../../../types/enums";
import { useAppSelector } from "../../../../hooks";
import { ApproximateTimeLabel } from "../../ApproximateTimeLabel";

type StatusCounts = {
  failed: number;
  in_progress: number;
  completed: number;
};

const getStatusCounts = (
  list: { title: string; items: Record<string, Activities[]> }[],
) => {
  return list.reduce(
    (acc, { items }) => {
      acc.in_progress +=
        (items["in_progress"]?.length || 0) + (items["queued"]?.length || 0);
      acc.failed += items["failed"]?.length || 0;
      acc.completed += items["completed"]?.length || 0;
      return acc;
    },
    { failed: 0, in_progress: 0, completed: 0 },
  );
};

const getMaxApproximateTime = (
  list: { title: string; items: Record<string, Activities[]> }[],
): number => {
  return list.reduce((max, { items }) => {
    const pending = [
      ...(items["in_progress"] ?? []),
      ...(items["queued"] ?? []),
    ];
    const localMax = pending.reduce(
      (acc, item) => Math.max(acc, item.approximate_time ?? 0),
      0,
    );
    return Math.max(max, localMax);
  }, 0);
};

const getPendingItemsKey = (
  list: { title: string; items: Record<string, Activities[]> }[],
): string => {
  return list
    .flatMap(({ items }) => [
      ...(items["in_progress"] ?? []),
      ...(items["queued"] ?? []),
    ])
    .map((item) => item.id)
    .sort()
    .join(",");
};

const getIcon = ({ failed, in_progress, completed }: StatusCounts) => {
  if (!!in_progress)
    return (
      <CircularProgress size={20} sx={{ color: "#3279B3", flexShrink: 0 }} />
    );

  if (!completed && !!failed)
    return <AlertInCircleIcon width="20px" height="20px" />;

  if (!!completed && !failed) return <CheckInEmptyCircleIcon />;

  if (!!completed && !!failed)
    return <AlertInCircleIcon width="20px" height="20px" color="#0860A8" />;
};

const CountTitle = ({
  counts,
  approximateTimeId,
  approximateTime,
  approximateTimeKey,
}: {
  counts: StatusCounts;
  approximateTimeId: string;
  approximateTime?: number;
  approximateTimeKey: string;
}) => {
  const { failed, in_progress, completed } = counts;
  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <Typography variant="body1" color="#1F2932">
        {completed} of {completed + in_progress + failed} finished
      </Typography>
      <ApproximateTimeLabel
        id={approximateTimeId}
        seconds={approximateTime}
        itemsKey={approximateTimeKey}
      />
    </Stack>
  );
};

export const NotificationSection = ({
  title,
  campaign_id,
  initialExpanded,
  showList,
  setOpenAccordion,
  setShowList,
  activities,
  type,
}: {
  title: string;
  campaign_id: string;
  initialExpanded: boolean;
  showList: Activities[];
  setOpenAccordion: (accordion: string) => void;
  setShowList: (list: Activities[]) => void;
  activities: { title: string; items: Record<string, Activities[]> }[];
  type: NotificationTypes;
}) => {
  const { isEditor, userEmail } = useAppSelector(
    (state) => state.company.userRoles,
  );

  const statusCounts = getStatusCounts(activities);
  const approximateTime =
    type === NotificationTypes.analysis
      ? getMaxApproximateTime(activities)
      : undefined;
  const approximateTimeKey = getPendingItemsKey(activities);
  const [deleteActivities, { isLoading: isDeleteActivities }] =
    useDeleteCompaniesActivitiesMutation();

  const deleteActivitiesHandler = () => {
    const account_ids = showList.map(({ id }) => id);
    deleteActivities({ campaign_id, type, account_ids })
      .unwrap()
      .then(() => {
        setShowList([]);
      });
  };

  const isScraping = type === "scraping";

  const values: Record<string, string> = {
    failed: isScraping ? "No Contacts Found" : "Failed",
    in_progress: "In progress",
    completed: "Success",
  };

  if (activities.length === 0) return null;

  return (
    <ReuseAccordion
      initialExpanded={initialExpanded}
      onExpandedHandler={() => setOpenAccordion(title)}
      header={(expanded) => (
        <AccordionHeader
          titleIcon={getIcon(statusCounts)}
          bodyBlock={
            <CountTitle
              counts={statusCounts}
              approximateTimeId={`${campaign_id}:${type}:section`}
              approximateTime={approximateTime}
              approximateTimeKey={approximateTimeKey}
            />
          }
          expanded={expanded}
          title={title}
        />
      )}
    >
      {showList.length > 0 ? (
        <Stack flexGrow={1} gap="8px">
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            paddingTop="8px"
            paddingRight="8px"
          >
            <IconButton sx={{ padding: "4px" }} onClick={() => setShowList([])}>
              <ChevronLeft />
            </IconButton>
            <Stack flexDirection="row" gap="8px">
              <StatusIcon status={showList[0].status} />
              <Typography variant="body1" fontWeight={500} color="#1F2932">
                {values[showList[0].status]} ({showList.length}),{" "}
                <span style={{ color: "#4C545B" }}>
                  {formattedDate(showList[0].date)}
                </span>
              </Typography>
            </Stack>
            {isDeleteActivities ? (
              <CircularProgress
                size={20}
                sx={{ color: "#3279B3", flexShrink: 0 }}
              />
            ) : (
              <div>
                {showList.every(({ status }) => status !== "in_progress") &&
                  isEditor && (
                    <span
                      onClick={deleteActivitiesHandler}
                      style={{
                        cursor: "pointer",
                        color: "#1F293299",
                        textDecoration: "underline",
                      }}
                    >
                      Clear all
                    </span>
                  )}
              </div>
            )}
          </Stack>
          <Stack
            flexGrow={1}
            padding="12px 10px"
            gap="8px"
            height="calc(100vh - 382px)"
            maxHeight="432px"
            overflow="auto"
          >
            {showList.map((item) => (
              <InfoItem
                key={item.name}
                company={item}
                campaign_id={campaign_id}
                isScraping={isScraping}
                type={type}
                isEditor={isEditor}
              />
            ))}
          </Stack>
        </Stack>
      ) : (
        <Box height="calc(100vh - 400px)" maxHeight="480px" overflow="auto">
          {activities?.map(({ title, items }) => (
            <NotificationBody
              type={type}
              campaign_id={campaign_id}
              key={title + Date.now()}
              title={title}
              items={items}
              setShowList={setShowList}
              isEditor={isEditor}
              userEmail={userEmail}
            />
          ))}
        </Box>
      )}
    </ReuseAccordion>
  );
};
